/****************
 * THE SPEED RUN, A TO Z: ONE PATH FOR THE WEB AND THE PHONE (Guillaume 11-01:
 * "This A to Z Speedrun set is valid for Web AND Mobile in the EXACT SAME WAY").
 *
 * Byte-identical twin in frontend/src/lib and mobile-app/src/lib. Each app's
 * guide engine reads this table (web: components/onboarding/IntentGuide.tsx,
 * phone: components/tour/QuickLearnTour.tsx); the screens only mark their
 * controls and send signals.
 *
 * His rules, kept by every step:
 *   - Nothing moves by itself. The Orb shows the control (it glows) and the
 *     person presses it: "no automatic navigation should ever happen, the goal
 *     is to show user how to do it not to do it for him".
 *   - All along, the Orb invites the person to talk to it (`orb`).
 *
 * A control is marked with its target name: data-tour="<target>" on the web,
 * testID "<target>" on the phone (the names the tours already use). A page is
 * a logical name; each engine maps it to its own addresses (RunPage).
 * Signals: runSignal(name) from lib/speedRun.ts (both apps).
 ****************/

/** The pages of the run. Each app maps them to its addresses (web /configure, phone /strategy...). */
export type RunPage = 'dashboard' | 'traders' | 'trader' | 'strategies' | 'copies' | 'learn' | 'backtest' | 'any';

export interface RunStep {
  /** Stable id: analytics, and the i18n keys guide:run.<id>.title / .body. */
  id: string;
  /** Where the step lives. */
  page: RunPage;
  /** The control that glows: the first of the list on screen (a tab before the
   *  button it shows); null: a card in the middle of the screen. */
  target: string | string[] | null;
  /** What makes the step go on: reaching a page, or runSignal(name). null: the card's Next. */
  doneWhen: { page?: RunPage; signal?: string } | null;
  /** The card offers "Ask the Orb" (Guillaume: "All along we should try to have him talk to the Orb"). */
  orb?: boolean;
  /*
   * A Yes / No question on the card (Guillaume 11-02: "ask him if the parameters are
   * all right or if he wants to change them ... just have some Yes / No buttons").
   * Text: guide run.<id>.q, and run.<id>.no shown after No. Yes goes on (or, on a
   * step that waits for an action, points at the glowing control); No shows the
   * hint, and with `back` offers to go back to that step; with `noGoesOn`, No goes on.
   */
  ask?: { back?: string; noGoesOn?: boolean };
}

/** The signals the screens send (runSignal). */
export const RUN_SIGNAL = {
  favorited: 'favorited',
  templateApplied: 'template-applied',
  strategySaved: 'strategy-saved',
  backtestStarted: 'backtest-started',
  paperStarted: 'paper-started',
  goalsSet: 'goals-set',
  backtestDone: 'backtest-done',
  proOpened: 'pro-opened',
} as const;

/** The builder moved to its page n (1 Basics ... 5 Review): runSignal(builderPage(n)). */
export const builderPage = (n: number) => `builder-page-${n}`;

/** What My Copies is filled with during a run (owner: the My Copies forms). */
export const RUN_DEFAULTS = {
  backtestName: 'First backtest',
  // One month (Guillaume 11-03): three ran close to the backtester's 20-minute limit (#64 stopped).
  periodDays: 30,
  capital: 1000,
  sessionKind: 'paper' as const,
};

export const SPEED_RUN_PATH: RunStep[] = [
  // Land on the Dashboard; the Orb shows up and explains it.
  { id: 'dash', page: 'dashboard', target: null, doneWhen: null, orb: true },
  { id: 'dash-kpis', page: 'dashboard', target: 'dash-kpis', doneWhen: null },
  // To the traders: the menu button glows, the person presses it.
  { id: 'go-traders', page: 'dashboard', target: 'nav-traders', doneWhen: { page: 'traders' } },
  { id: 'pick-trader', page: 'traders', target: 'traders-featured', doneWhen: { page: 'trader' } },
  // His profile and some performance metrics, explained; does he suit you?
  { id: 'trader-metrics', page: 'trader', target: 'tr-metrics', doneWhen: null, orb: true },
  { id: 'trader-ok', page: 'trader', target: 'tr-metrics', doneWhen: null, ask: { back: 'go-traders' } },
  { id: 'favorite', page: 'trader', target: 'tr-star', doneWhen: { signal: RUN_SIGNAL.favorited } },
  // To the strategies, by pressing the glowing button.
  { id: 'go-strategy', page: 'trader', target: 'nav-strategy', doneWhen: { page: 'strategies' } },
  // A template proposed from the trader; each page of the builder explained, the
  // person says whether the values suit them, then saves.
  { id: 'template', page: 'strategies', target: 'sb-run-template', doneWhen: { signal: RUN_SIGNAL.templateApplied }, orb: true },
  { id: 'b-basics', page: 'strategies', target: ['sb-continue', 'sb-content'], doneWhen: { signal: builderPage(2) }, ask: {} },
  { id: 'b-risk', page: 'strategies', target: ['sb-continue', 'sb-content'], doneWhen: { signal: builderPage(3) }, ask: {} },
  { id: 'b-orders', page: 'strategies', target: ['sb-continue', 'sb-content'], doneWhen: { signal: builderPage(4) }, ask: {} },
  { id: 'b-exit', page: 'strategies', target: ['sb-continue', 'sb-content'], doneWhen: { signal: builderPage(5) }, ask: {} },
  { id: 'save', page: 'strategies', target: ['sb-create', 'sb-continue'], doneWhen: { signal: RUN_SIGNAL.strategySaved } },
  // To My Copies: it opens on the backtest, filled in; are the settings right? then start it.
  { id: 'go-copies', page: 'strategies', target: 'nav-sessions', doneWhen: { page: 'copies' } },
  { id: 'backtest', page: 'copies', target: ['bt-run', 'ct-tab-backtest'], doneWhen: { signal: RUN_SIGNAL.backtestStarted }, orb: true, ask: {} },
  // While it runs: Go Live, a paper session, filled in.
  { id: 'paper', page: 'copies', target: ['ct-start', 'ct-tab-live'], doneWhen: { signal: RUN_SIGNAL.paperStarted }, ask: {} },
  // A few questions, and goals in the achievements.
  { id: 'questions', page: 'any', target: null, doneWhen: null, orb: true },
  { id: 'goals', page: 'any', target: ['ach-goals', 'ach-open-page', 'header-achievements'], doneWhen: { signal: RUN_SIGNAL.goalsSet } },
  // Learn: what they have access to.
  { id: 'go-learn', page: 'any', target: 'nav-learn', doneWhen: { page: 'learn' } },
  { id: 'learn', page: 'learn', target: null, doneWhen: null, orb: true },
  // The backtest is done: a notification says so, and opens the result.
  { id: 'go-result', page: 'any', target: null, doneWhen: { signal: RUN_SIGNAL.backtestDone } },
  { id: 'open-result', page: 'any', target: ['notif-backtest-ready', 'nav-notifications'], doneWhen: { page: 'backtest' } },
  // The result explained, then Adjust and re-run.
  { id: 'result', page: 'backtest', target: ['bt-summary', 'bt-history'], doneWhen: null, orb: true },
  { id: 'rerun', page: 'backtest', target: 'bt-adjust', doneWhen: null, ask: { noGoesOn: true } },
  // CopyFi Pro unlocked: to Pro, then the regular quick tour.
  { id: 'go-pro', page: 'any', target: 'switch-pro', doneWhen: { signal: RUN_SIGNAL.proOpened } },
];

/** The step that ends the run's lock on CopyFi Pro (it opens on this one). */
export const RUN_PRO_STEP = 'go-pro';
